// storageUtils.ts
import { storage } from './firebase';

/**
 * Converts a Base64 encoded string to a Blob object.
 * This is necessary for uploading images captured from the camera.
 * @param base64 The Base64 string (e.g., from a data URL).
 * @param mimeType The MIME type of the image (e.g., 'image/jpeg').
 * @returns A Blob object representing the image.
 */
export const base64ToBlob = (base64: string, mimeType: string): Blob => {
    // Remove the data URL prefix if it exists
    const base64Data = base64.split(',')[1] || base64;
    
    try {
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: mimeType });
    } catch (error) {
        console.error("Error converting base64 to Blob:", error);
        // Return an empty blob if conversion fails
        return new Blob([], { type: mimeType });
    }
};

/**
 * Uploads a file or Blob to a specified path in Firebase Storage.
 * @param file The File or Blob to upload.
 * @param path The base path in storage (e.g., 'profile-pictures/userId').
 * @returns A promise that resolves with the public download URL of the uploaded file.
 */
export const uploadFile = async (file: File | Blob, path: string): Promise<string> => {
    // Generate a unique filename to prevent overwrites
    const uniqueFileName = `${Date.now()}-${Math.random().toString(36).substring(2)}`;
    const fullPath = `${path}/${uniqueFileName}`;
    
    const storageRef = storage.ref(fullPath);
    const snapshot = await storageRef.put(file);
    const downloadURL = await snapshot.ref.getDownloadURL();
    
    return downloadURL;
};

/**
 * Helper function to fetch a URL and convert it to a base64 data URL.
 * Uses the HTML5 Image object and Canvas to bypass some fetch API restrictions.
 * @param url The public URL of the image.
 * @returns A promise that resolves with the base64 data URL string or null if it fails.
 */
export const urlToBase64 = (url: string): Promise<string | null> => {
    return new Promise((resolve) => {
        const img = new Image();
        // Request CORS permission. If the bucket is configured correctly, this allows canvas export.
        img.crossOrigin = 'Anonymous';
        
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.drawImage(img, 0, 0);
                    // Convert to base64
                    const dataURL = canvas.toDataURL('image/jpeg');
                    resolve(dataURL);
                } else {
                    console.error("Could not get canvas context for image conversion.");
                    resolve(null);
                }
            } catch (e) {
                console.error("Canvas export failed. This is usually due to CORS configuration on the Firebase Storage bucket.", e);
                resolve(null);
            }
        };

        img.onerror = (e) => {
            console.error("Failed to load image for Base64 conversion:", e);
            resolve(null);
        };

        img.src = url;
    });
};